import { InfoOutlined } from "@mui/icons-material";
import { Grid, Typography } from "@mui/material";
import AcxHierarchySelector from "components/UI/AcxHierarchySelector/AcxHierarchySelector";
import AcxInputLabel from "components/UI/AcxInputLabel";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxVirtualizedSelectMulti from "components/UI/Select/AcxVirtualizedSelectMulti";
import AcxVirtualizedSelectMultiGrouped from "components/UI/Select/AcxVirtualizedSelectMultiGrouped";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import theme from "Theme/AppTheme";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import {
    groupBySelectionLimitMessage,
    MAX_GROUP_BY_SELECTIONS,
} from "../../../Grouping/groupByDraft";
import type { CategoricalGroupByConfiguration } from "../../../SignalsModels";
import ChartCardStore from "../../ChartCard/ChartCardStore";
import {
    configureInputLabelSx,
    SELECTION_LIMIT_FALLBACK_OPTIONS,
} from "./groupByOptionConfig";
import {
    useCategoricalValueOptions,
    type GroupValueOption,
} from "./useCategoricalValueOptions";

type Props = {
    chartStore: ChartCardStore;
    /**
     * The grouping being configured. Owned by the coordinator, which derives it from the drawer's
     * vizOptions -- this component holds no grouping state of its own and re-renders with a new
     * object after every edit.
     */
    draft: CategoricalGroupByConfiguration;
    /** Reports an edited draft upward. Stable identity, so effects can depend on it. */
    onDraftChange: (draft: CategoricalGroupByConfiguration) => void;
    /** Plural label above the value picker, e.g. "Agents". */
    valueLabel: string;
    /** Singular noun the value picker uses in its own copy, e.g. "Agent". */
    itemName: string;
    /** Set when the draft is missing a prerequisite or exceeds the selection limit. */
    validationMessage?: string;
};

/**
 * Configures a categorical grouping: which dimension values the chart groups by, plus the context a
 * few dimensions need first (a model for Model Results, a field for Extended Metadata).
 *
 * Sourcing those values lives in `useCategoricalValueOptions`; this component is the pickers, the
 * prerequisite selector, and keeping the draft's ids in step with what the pickers can offer.
 */
export const CategoricalGroupByEditor: React.FC<Props> = observer(
    ({
        chartStore,
        draft,
        onDraftChange,
        valueLabel,
        itemName,
        validationMessage,
    }) => {
        const authStore = useStore(AuthStore);

        const {
            valueOptions,
            valuesLoading,
            canValidateSelectedIds,
            groupedAgentValueOptions,
            agentsLoading,
            questionResult,
        } = useCategoricalValueOptions(chartStore, draft);

        const selectedGroupByOption = draft.option;

        const selectedQuestionResultModule = questionResult.moduleGroups
            .flatMap((group) => group.options)
            .find((option) => option.id === draft.licensedModuleId);
        const selectedQuestionResultQuestion = questionResult.questionOptions.find(
            (option) => option.id === draft.questionId,
        );

        const groupByModelOptions: GroupValueOption[] =
            chartStore.optionsStore.llmClassifiers.map((classifier) => ({
                id: classifier.id,
                label: classifier.name,
            }));
        const selectedModelOption = groupByModelOptions.find(
            (option) => option.id === draft.llmClassifierId,
        );

        /**
         * The saved selections, minus any the current option can no longer offer -- values deleted
         * since the chart was configured, or left over from a switch of model or metadata field.
         * Dropping them keeps the picker's state and the draft in agreement.
         *
         * Only filtered when the option's values are actually knowable: a dimension with its own
         * selector, an unchosen prerequisite, or a list still loading all mean "not in valueOptions"
         * does not imply "invalid", and filtering then would discard the user's selections.
         */
        const sanitizedSelectedIds = (() => {
            const currentIds = draft.valueIds;

            if (selectedGroupByOption === "Hierarchy") {
                return currentIds.slice(0, MAX_GROUP_BY_SELECTIONS);
            }

            if (!canValidateSelectedIds || valuesLoading) {
                return currentIds;
            }

            const validIds = new Set(valueOptions.map((option) => option.id));
            return currentIds.filter((id) => validIds.has(id));
        })();

        useEffect(() => {
            // Write the sanitized ids back so Save persists what the picker is showing. `draft` is
            // a dependency and this writes a new draft, so the equality check is what stops the
            // loop: after one pass the ids match and the effect no-ops.
            if (
                draft.valueIds.length === sanitizedSelectedIds.length &&
                draft.valueIds.every(
                    (id, index) => id === sanitizedSelectedIds[index],
                )
            ) {
                return;
            }

            onDraftChange({ ...draft, valueIds: sanitizedSelectedIds });
        }, [draft, onDraftChange, sanitizedSelectedIds]);

        // Warn about the cap wherever it is reachable: either the list is already long enough to
        // hit it, or it is an option whose list is long in practice but may not have loaded yet.
        const shouldShowSelectionLimitNotice =
            (selectedGroupByOption === "Agent"
                ? groupedAgentValueOptions.length
                : valueOptions.length) > MAX_GROUP_BY_SELECTIONS ||
            SELECTION_LIMIT_FALLBACK_OPTIONS.has(selectedGroupByOption);

        const handleValueIdsChange = (ids?: string[]) => {
            onDraftChange({ ...draft, valueIds: ids ?? [] });
        };

        return (
            <Grid container direction="column" rowGap={2}>
                {/*
                 * Hierarchy brings its own tree selector and replaces the value picker outright;
                 * Model Results adds a model selector above it. Everything else uses the generic
                 * multi-select below.
                 */}
                {selectedGroupByOption === "Hierarchy" && (
                    <Grid item>
                        <AcxHierarchySelector
                            userId={authStore._user.profile.sub}
                            orgId={
                                authStore.orgStore.selectedOrganization?.id ?? ""
                            }
                            displayType="input"
                            setHierarchyIds={handleValueIdsChange}
                            onSaveUpdateWithHierarchies={handleValueIdsChange}
                            labelText={valueLabel}
                            width="100%"
                            removeMinWidth
                            initSelectedHierarchyIds={draft.valueIds}
                            customZIndex={theme.zIndex.appBar + 1}
                            popperPlacement="left"
                            maxSelectedHierarchyCount={MAX_GROUP_BY_SELECTIONS}
                            selectionLimitMessage={groupBySelectionLimitMessage}
                        />
                    </Grid>
                )}

                {selectedGroupByOption === "ModelResults" && (
                    <Grid item>
                        <AcxSelectSingle
                            id={`${chartStore.id}-group-by-model-selector`}
                            inputLabel="Model"
                            options={groupByModelOptions}
                            defaultValue={selectedModelOption}
                            onChange={(option?: GroupValueOption) => {
                                onDraftChange({
                                    ...draft,
                                    llmClassifierId: option?.id,
                                    valueIds: [],
                                });
                            }}
                            valueField="id"
                            labelField="label"
                            fullWidth
                            isSearchable
                            isClearable
                            placeholder="Select model"
                            isLoading={
                                chartStore.optionsStore.llmClassifiersLoading
                            }
                        />
                    </Grid>
                )}

                {/*
                 * Question Result needs a module and then a question before its answer options mean
                 * anything. Each selector clears everything downstream of it, so the value picker
                 * below never shows options belonging to a question that is no longer selected.
                 */}
                {selectedGroupByOption === "QuestionResult" && (
                    <>
                        <Grid item>
                            <AcxSelectSingle
                                id={`${chartStore.id}-group-by-question-result-module`}
                                inputLabel="Module"
                                options={questionResult.moduleGroups}
                                defaultValue={selectedQuestionResultModule}
                                onChange={(option?: GroupValueOption) => {
                                    if (option?.id === draft.licensedModuleId) {
                                        return;
                                    }

                                    onDraftChange({
                                        ...draft,
                                        licensedModuleId: option?.id,
                                        questionId: undefined,
                                        valueIds: [],
                                    });
                                }}
                                valueField="id"
                                labelField="label"
                                fullWidth
                                isSearchable
                                isClearable
                                placeholder="Select module"
                                isLoading={questionResult.modulesLoading}
                            />
                        </Grid>
                        <Grid item>
                            <AcxSelectSingle
                                id={`${chartStore.id}-group-by-question-result-question`}
                                inputLabel="Question"
                                options={questionResult.questionOptions}
                                defaultValue={selectedQuestionResultQuestion}
                                onChange={(option?: GroupValueOption) => {
                                    if (option?.id === draft.questionId) {
                                        return;
                                    }

                                    onDraftChange({
                                        ...draft,
                                        questionId: option?.id,
                                        valueIds: [],
                                    });
                                }}
                                valueField="id"
                                labelField="label"
                                fullWidth
                                isSearchable
                                isClearable
                                isDisabled={!draft.licensedModuleId}
                                placeholder={
                                    draft.licensedModuleId
                                        ? "Select question"
                                        : "Select a module first"
                                }
                                isLoading={questionResult.questionsLoading}
                            />
                        </Grid>
                    </>
                )}

                {selectedGroupByOption !== "Hierarchy" && (
                    <Grid item>
                        <Grid container direction="column" rowGap={0.75}>
                            <Grid item>
                                <AcxInputLabel
                                    htmlFor={`${chartStore.id}-group-by-value-select`}
                                    labelText={valueLabel}
                                    sx={configureInputLabelSx}
                                />
                            </Grid>
                            <Grid item>
                                {selectedGroupByOption === "Agent" ? (
                                    <AcxVirtualizedSelectMultiGrouped
                                        id={`${chartStore.id}-group-by-value-select`}
                                        defaultSelectedIds={sanitizedSelectedIds}
                                        onApply={handleValueIdsChange}
                                        maxSelectionCount={
                                            MAX_GROUP_BY_SELECTIONS
                                        }
                                        options={groupedAgentValueOptions}
                                        isLoading={agentsLoading}
                                        itemName={itemName}
                                        labelField="fullName"
                                        warningMessage={
                                            groupBySelectionLimitMessage
                                        }
                                    />
                                ) : (
                                    <AcxVirtualizedSelectMulti
                                        id={`${chartStore.id}-group-by-value-select`}
                                        defaultSelectedIds={sanitizedSelectedIds}
                                        onApply={handleValueIdsChange}
                                        maxSelectionCount={
                                            MAX_GROUP_BY_SELECTIONS
                                        }
                                        options={valueOptions}
                                        labelField="label"
                                        idField="id"
                                        isLoading={valuesLoading}
                                        itemName={itemName}
                                        warningMessage={
                                            groupBySelectionLimitMessage
                                        }
                                    />
                                )}
                            </Grid>
                            {validationMessage && (
                                <Grid item>
                                    <Typography
                                        variant="body2"
                                        color={theme.palette.error.main}
                                    >
                                        {validationMessage}
                                    </Typography>
                                </Grid>
                            )}
                            {shouldShowSelectionLimitNotice && (
                                <Grid item>
                                    <Grid
                                        container
                                        alignItems="center"
                                        wrap="nowrap"
                                        columnGap={0.75}
                                    >
                                        <Grid item>
                                            <InfoOutlined
                                                sx={{
                                                    fontSize: 18,
                                                    color: theme.palette.blue
                                                        .main,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.blue
                                                        .main,
                                                }}
                                            >
                                                {groupBySelectionLimitMessage}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                )}
            </Grid>
        );
    },
);

export default CategoricalGroupByEditor;
